import { View, Text, Platform } from 'react-native';
import { useRouter, Stack } from 'expo-router';
import { Home, ArrowLeft } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import AnimatedPressable from '@/components/AnimatedPressable';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { PublicLayout } from '@/components/PublicLayout';
import { SafeAreaView } from 'react-native-safe-area-context';

export default function NotFoundScreen() {
  useTrackScreen('NotFound');
  const router = useRouter();
  const colors = usePublicThemeColors();

  const content = (
    <View className="items-center justify-center px-8 py-16">
      <Animated.View entering={FadeInDown.delay(100).duration(500)}>
        <Text
          style={{
            fontSize: 72,
            fontWeight: '800',
            color: colors.primary[600],
            letterSpacing: -2,
          }}
        >
          404
        </Text>
      </Animated.View>

      <Animated.View entering={FadeInDown.delay(200).duration(500)} className="mt-4">
        <Text
          style={{
            fontSize: 24,
            fontWeight: '700',
            color: colors.text.primary,
            textAlign: 'center',
          }}
        >
          Page not found
        </Text>
      </Animated.View>

      <Animated.View entering={FadeInDown.delay(300).duration(500)} className="mt-3">
        <Text
          style={{
            fontSize: 16,
            color: colors.text.secondary,
            textAlign: 'center',
            lineHeight: 24,
            maxWidth: 320,
          }}
        >
          The page you're looking for doesn't exist or has been moved.
        </Text>
      </Animated.View>

      <Animated.View
        entering={FadeInDown.delay(400).duration(500)}
        className="flex-row gap-3 mt-8"
      >
        <AnimatedPressable
          onPress={() => router.replace('/')}
          className="flex-row items-center gap-2 px-6 py-3.5 rounded-xl"
          style={{ backgroundColor: colors.primary[600] }}
        >
          <Home size={18} color="#FFFFFF" />
          <Text style={{ color: '#FFFFFF', fontWeight: '600', fontSize: 15 }}>
            Go Home
          </Text>
        </AnimatedPressable>

        <AnimatedPressable
          onPress={() => router.back()}
          className="flex-row items-center gap-2 px-6 py-3.5 rounded-xl"
          style={{
            borderWidth: 1.5,
            borderColor: colors.border.medium,
            backgroundColor: colors.surface.primary,
          }}
        >
          <ArrowLeft size={18} color={colors.text.secondary} />
          <Text style={{ color: colors.text.secondary, fontWeight: '600', fontSize: 15 }}>
            Go Back
          </Text>
        </AnimatedPressable>
      </Animated.View>
    </View>
  );

  // On web, wrap in PublicLayout for consistent nav/footer
  if (Platform.OS === 'web') {
    return (
      <>
        <Stack.Screen options={{ title: 'Page Not Found', headerShown: false }} />
        <PublicLayout title="Page Not Found" wrapContent={false}>
          <View style={{ minHeight: 400, justifyContent: 'center' }}>
            {content}
          </View>
        </PublicLayout>
      </>
    );
  }

  // On mobile, simple centered layout
  return (
    <>
      <Stack.Screen options={{ title: 'Page Not Found', headerShown: false }} />
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        <SafeAreaView edges={['top', 'bottom']} className="flex-1 justify-center">
          {content}
        </SafeAreaView>
      </View>
    </>
  );
}
